import React from 'react';
import PropTypes from 'prop-types';
import Bowser from 'bowser';

/**
 * Component to display a notice for users with outdated browsers
 */
const OutdatedBrowserNotice = ({ onContinue }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 text-white rounded-lg p-6 max-w-lg w-full border border-gray-700 shadow-2xl">
        <div className="flex items-center mb-4">
          <svg className="w-8 h-8 text-yellow-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <h2 className="text-2xl font-bold text-white">Outdated Browser Detected</h2>
        </div>
        
        <div className="mb-6 bg-gray-800 p-4 rounded border-l-4 border-yellow-500">
          <p className="mb-2">
            You're using an outdated browser or device that isn't fully compatible with our site.
          </p>
          <p className="text-gray-300">
            This may cause errors, prevent certain features from working correctly, or result in a poor viewing experience.
          </p>
        </div>
        
        <p className="mb-4 text-gray-300">
          For the best experience, we recommend:
        </p>
        
        <ul className="list-disc pl-5 mb-6 space-y-2 text-gray-300">
          <li>Update your browser to the latest version</li>
          <li>Update your device's operating system</li>
          <li>Try using a modern browser like Chrome, Firefox, Safari, or Edge</li>
          <li>If using a mobile device, try the standalone browser app instead of WebView</li>
        </ul>
        
        {/* Browser detection and reporting */}
        {(() => {
          let browserName = '';
          let browserVersion = '';
          let ua = '';
          try {
            if (typeof window !== 'undefined' && window.navigator && window.navigator.userAgent) {
              ua = window.navigator.userAgent;
              const browser = Bowser.getParser(window.navigator.userAgent);
              browserName = browser.getBrowserName() || '';
              browserVersion = browser.getBrowserVersion() || '';
            }
          } catch (_e) {
            // fallback to UA string
          }
          return (
            <div className="text-xs text-gray-400 text-center mb-2">
              {browserName ? (
                <>Detected browser: <span className="font-semibold">{browserName} {browserVersion}</span></>
              ) : ua ? (
                <>Detected user agent: <span className="font-mono break-all">{ua}</span></>
              ) : (
                <>Unable to detect browser</>
              )}
            </div>
          );
        })()}

        <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-3">
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
            <a 
              href="https://www.google.com/chrome/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-center transition-colors text-sm"
            >
              Get Chrome
            </a>
            <a 
              href="https://www.mozilla.org/firefox/new/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-orange-600 text-white px-3 py-2 rounded hover:bg-orange-700 text-center transition-colors text-sm"
            >
              Get Firefox
            </a>
            <a 
              href="https://www.microsoft.com/edge" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-teal-600 text-white px-3 py-2 rounded hover:bg-teal-700 text-center transition-colors text-sm"
            >
              Get Edge
            </a>
          </div>
          <button 
            onClick={onContinue}
            className="bg-gray-700 text-gray-300 px-4 py-2 rounded hover:bg-gray-600 transition-colors"
          >
            Continue Anyway
          </button>
        </div>
        <p className="mt-4 text-xs text-gray-500 text-center">
          Note: Continuing with an unsupported browser may result in errors or limited functionality.
        </p>
      </div>
    </div>
  );
};

OutdatedBrowserNotice.propTypes = {
  onContinue: PropTypes.func.isRequired,
};

export default OutdatedBrowserNotice;
